import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import { Button } from "../../../components/ui/Button";
import { Select } from "../../../components/ui/Select";
import { Input } from "../../../components/ui/Input";
import { formatCurrency } from "../../../utils/localization";
import apiService from "../../../services/api";

const SettleUpModal = ({
  isOpen,
  onClose,
  balances,
  onRecordPayment,
  eventId,
}) => {
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [isProcessing, setIsProcessing] = useState(false);

  const debtors = balances?.filter((b) => b?.amount < -0.01);
  const creditors = balances?.filter((b) => b?.amount > 0.01);

  const generateSettlements = () => {
    const settlements = [];

    debtors?.forEach((debtor) => {
      creditors?.forEach((creditor) => {
        const maxSettlement = Math.min(
          Math.abs(debtor?.amount),
          creditor?.amount
        );
        if (maxSettlement > 0.01) {
          settlements?.push({
            id: `${debtor?.participant?.id}-${creditor?.participant?.id}`,
            from: debtor?.participant,
            to: creditor?.participant,
            amount: maxSettlement,
            description: `${debtor?.participant?.name} pays ${creditor?.participant?.name}`,
          });
        }
      });
    });

    return settlements?.sort((a, b) => b?.amount - a?.amount);
  };

  const settlements = generateSettlements();

  const paymentMethodOptions = [
    { value: "cash", label: "Cash" },
    { value: "venmo", label: "Venmo" },
    { value: "paypal", label: "PayPal" },
    { value: "zelle", label: "Zelle" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "other", label: "Other" },
  ];

  const handleSettlementSelect = (settlement) => {
    setSelectedSettlement(settlement);
    setPaymentAmount(settlement?.amount?.toFixed(2));
  };

  const handleRecordPayment = async () => {
    if (!selectedSettlement || !paymentAmount) return;

    setIsProcessing(true);

    try {
      // Create settlement via API
      const settlementData = {
        paidBy: selectedSettlement?.from?.id,
        paidTo: selectedSettlement?.to?.id,
        amount: parseFloat(paymentAmount),
        method: paymentMethod,
        description: `Settlement payment from ${selectedSettlement?.from?.name} to ${selectedSettlement?.to?.name}`,
        date: new Date()?.toISOString(),
      };

      const response = await apiService.createSettlement(
        eventId,
        settlementData
      );

      if (response.success) {
        // Call the parent callback to refresh the event data
        await onRecordPayment(response.data.settlement);
        handleClose();
      } else {
        throw new Error(response.message || "Failed to record settlement");
      }
    } catch (error) {
      console.error("Error recording payment:", error);
      alert(error.message || "Failed to record settlement");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setSelectedSettlement(null);
    setPaymentAmount("");
    setPaymentMethod("cash");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Settle Up</h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        <div className="p-6 space-y-6">
          {settlements?.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                All Settled Up!
              </h3>
              <p className="text-muted-foreground">
                Everyone's balances are even. No payments needed.
              </p>
            </div>
          ) : (
            <>
              {/* Settlement Suggestions */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">
                  Suggested Payments
                </h3>
                <p className="text-sm text-muted-foreground">
                  Select a payment to record. This will update everyone's
                  balances.
                </p>

                <div className="space-y-3">
                  {settlements?.map((settlement) => (
                    <div
                      key={settlement?.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-micro ${
                        selectedSettlement?.id === settlement?.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                      onClick={() => handleSettlementSelect(settlement)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-foreground">
                                {settlement?.from?.name
                                  ?.charAt(0)
                                  ?.toUpperCase()}
                              </span>
                            </div>
                            <Icon
                              name="ArrowRight"
                              size={20}
                              className="text-muted-foreground"
                            />
                            <div className="w-10 h-10 bg-success rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-success-foreground">
                                {settlement?.to?.name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">
                              {settlement?.from?.name} → {settlement?.to?.name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Payment to settle balance
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-semibold text-foreground">
                            {formatCurrency(settlement?.amount)}
                          </p>
                          {selectedSettlement?.id === settlement?.id && (
                            <Icon
                              name="CheckCircle"
                              size={16}
                              className="text-primary ml-auto mt-1"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Details */}
              {selectedSettlement && (
                <div className="pt-6 border-t border-border space-y-4">
                  <h3 className="text-lg font-medium text-foreground">
                    Payment Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Payment Amount"
                      type="number"
                      step="0.01"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e?.target?.value)}
                      placeholder="0.00"
                      required
                    />

                    <Select
                      label="Payment Method"
                      options={paymentMethodOptions}
                      value={paymentMethod}
                      onChange={setPaymentMethod}
                      required
                    />
                  </div>

                  <div className="p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Info" size={16} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">
                        Payment Summary
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Recording a payment of{" "}
                      <span className="font-medium text-foreground">
                        {formatCurrency(parseFloat(paymentAmount) || 0)}
                      </span>{" "}
                      from{" "}
                      <span className="font-medium text-foreground">
                        {selectedSettlement?.from?.name}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium text-foreground">
                        {selectedSettlement?.to?.name}
                      </span>{" "}
                      via{" "}
                      <span className="font-medium text-foreground">
                        {
                          paymentMethodOptions?.find(
                            (p) => p?.value === paymentMethod
                          )?.label
                        }
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRecordPayment}
                  loading={isProcessing}
                  disabled={
                    !selectedSettlement ||
                    !paymentAmount ||
                    parseFloat(paymentAmount) <= 0
                  }
                  className="flex-1"
                >
                  Record Payment
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettleUpModal;
